import type { FlexibleSchedule } from './types'
import { REQUEST_BESPOKE_VALUE, LOCKDROPS_VALUE } from './constants'
import {
  getUrlErrorMessage,
  isValidHttpsUrl,
  isValidWalletAddress,
} from './validators'

const FIXED_POOL_NAME_PRESETS = ['Genesis Pool']
const FLEXIBLE_POOL_NAME_PRESETS = [
  'Standard Pool',
  'Early Unlock',
  'Long-Term Holder',
]

const isValidUtcInput = (value: unknown) => {
  const text = String(value || '').trim()
  return Boolean(text) && !Number.isNaN(new Date(`${text}Z`).getTime())
}

const isPositiveInt = (value: unknown) => {
  const text = String(value || '').trim()
  const n = Number(text)
  return Number.isSafeInteger(n) && n > 0 && String(n) === text
}

const isDecimals = (value: unknown) => {
  const text = String(value || '').trim()
  const n = Number(text)
  return Number.isInteger(n) && n >= 0 && n <= 255 && String(n) === text
}

const isNonNegativeNumber = (value: unknown) => {
  const text = String(value ?? '').trim()
  return Boolean(text) && Number.isFinite(Number(text)) && Number(text) >= 0
}

const isPositiveNumber = (value: unknown) =>
  isNonNegativeNumber(value) && Number(value) > 0

/** Lowercase letters and digits in hyphen-separated groups. */
export const isValidDealSlug = (value: unknown) =>
  /^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(String(value ?? '').trim())

export type V1DepositValidationOptions = {
  /** Also validate the TVL terms and deposit token price. */
  tvl?: boolean
  /**
   * Editing an existing deal: nothing is required, only values that were
   * filled in are checked.
   */
  edit?: boolean
  /** Fields the form cannot change (locked in edit mode); never flagged. */
  skipFields?: string[]
  /** Leading additional chains that are stored and locked in edit mode. */
  lockedConfigCount?: number
  /** Deposit and token addresses of stored chains can be changed. */
  addressesEditable?: boolean
}

/**
 * Only the deposit block is mandatory: some sales are deposit-only. Refund,
 * claim, sale token and pool size are validated only when something in that
 * section has been filled in. Additional deposit chains are validated per
 * row under `v1DepositConfigs.<index>.<field>`.
 */
export function validateV1Deposit(
  data: any,
  options: V1DepositValidationOptions = {},
): {
  errors: Record<string, string>
  isValid: boolean
  message?: string
} {
  const errors: Record<string, string> = {}
  const text = (field: string) => String(data[field] || '').trim()
  const anyFilled = (...fields: string[]) => fields.some((f) => text(f))
  // In edit mode a blank field is never an error.
  const check = (field: string, isValid: boolean, message: string) => {
    if (isValid || (options.edit && !text(field))) return
    errors[field] = message
  }
  const checkWindow = (start: string, end: string, message: string) => {
    if (
      isValidUtcInput(data[start]) &&
      isValidUtcInput(data[end]) &&
      new Date(`${data[end]}Z`) <= new Date(`${data[start]}Z`)
    )
      errors[end] = message
  }

  check(
    'v1DepositChainId',
    isPositiveInt(data.v1DepositChainId),
    'Enter a valid positive EVM chain ID',
  )
  check(
    'v1DepositAddress',
    isValidWalletAddress(text('v1DepositAddress')),
    'Enter a valid deposit contract address',
  )
  check(
    'depositTokenAddress',
    isValidWalletAddress(text('depositTokenAddress')),
    'Enter a valid deposit token address',
  )
  check(
    'depositTokenDecimals',
    isDecimals(data.depositTokenDecimals),
    'Enter deposit token decimals from 0 to 255',
  )
  check(
    'depositTokenName',
    Boolean(text('depositTokenName')),
    'Deposit token name is required',
  )
  check(
    'depositTokenSymbol',
    Boolean(text('depositTokenSymbol')),
    'Deposit token symbol is required',
  )
  check(
    'v1DepositStartTime',
    isValidUtcInput(data.v1DepositStartTime),
    'Enter a valid deposit start date and time',
  )
  check(
    'v1DepositEndTime',
    isValidUtcInput(data.v1DepositEndTime),
    'Enter a valid deposit end date and time',
  )
  checkWindow(
    'v1DepositStartTime',
    'v1DepositEndTime',
    'Deposit end must be after deposit start',
  )

  if (text('v1DepositPoolSize') && !/^\d+$/.test(text('v1DepositPoolSize')))
    errors.v1DepositPoolSize =
      'Enter the deposit pool size in token base units (digits only)'

  if (anyFilled('v1RefundStartTime', 'v1RefundEndTime')) {
    check(
      'v1RefundStartTime',
      isValidUtcInput(data.v1RefundStartTime),
      'Enter a valid refund start date and time',
    )
    check(
      'v1RefundEndTime',
      isValidUtcInput(data.v1RefundEndTime),
      'Enter a valid refund end date and time',
    )
    checkWindow(
      'v1RefundStartTime',
      'v1RefundEndTime',
      'Refund end must be after refund start',
    )
  }

  if (
    anyFilled(
      'v1ClaimChainId',
      'v1ClaimAddress',
      'v1ClaimTokenDecimals',
      'v1ClaimTokenSymbol',
    )
  ) {
    check(
      'v1ClaimChainId',
      isPositiveInt(data.v1ClaimChainId),
      'Enter a valid positive EVM chain ID',
    )
    check(
      'v1ClaimAddress',
      isValidWalletAddress(text('v1ClaimAddress')),
      'Enter a valid claim contract address',
    )
    check(
      'v1ClaimTokenDecimals',
      isDecimals(data.v1ClaimTokenDecimals),
      'Enter claim token decimals from 0 to 255',
    )
    check(
      'v1ClaimTokenSymbol',
      Boolean(text('v1ClaimTokenSymbol')),
      'Claim token symbol is required',
    )
  }

  if (
    anyFilled(
      'v1SaleTokenAddress',
      'v1SaleTokenDecimals',
      'v1SaleTokenName',
      'v1SaleTokenSymbol',
    )
  ) {
    if (
      text('v1SaleTokenAddress') &&
      !isValidWalletAddress(text('v1SaleTokenAddress'))
    )
      errors.v1SaleTokenAddress =
        'Enter a valid sale token address or leave blank'
    check(
      'v1SaleTokenDecimals',
      isDecimals(data.v1SaleTokenDecimals),
      'Enter sale token decimals from 0 to 255',
    )
    check(
      'v1SaleTokenName',
      Boolean(text('v1SaleTokenName')),
      'Sale token name is required',
    )
    check(
      'v1SaleTokenSymbol',
      Boolean(text('v1SaleTokenSymbol')),
      'Sale token symbol is required',
    )
  }

  for (const field of ['v1SaleId', 'v1AllocationSaleId', 'v1TrackId']) {
    if (text(field) && !/^\d+$/.test(text(field)))
      errors[field] = 'Enter a whole number'
  }

  // Additional deposit chains. Stored rows are locked in edit mode, so only
  // their editable token details are checked; a newly added chain must always
  // be complete because a half-configured chain cannot take deposits.
  const configs: any[] = Array.isArray(data.v1DepositConfigs)
    ? data.v1DepositConfigs
    : []
  const lockedConfigCount = options.edit ? options.lockedConfigCount ?? 0 : 0
  const chainKey = (chainId: unknown, address: unknown) =>
    `${String(chainId || '').trim()}:${String(address || '')
      .trim()
      .toLowerCase()}`
  const seenChains = new Set([
    chainKey(data.v1DepositChainId, data.v1DepositAddress),
  ])
  configs.forEach((row, index) => {
    const value = (field: string) => String(row?.[field] || '').trim()
    const isStored = index < lockedConfigCount
    const rowCheck = (field: string, isValid: boolean, message: string) => {
      if (isValid || (isStored && !value(field))) return
      errors[`v1DepositConfigs.${index}.${field}`] = message
    }
    rowCheck(
      'tokenDecimals',
      isDecimals(row?.tokenDecimals),
      'Enter deposit token decimals from 0 to 255',
    )
    rowCheck(
      'tokenName',
      Boolean(value('tokenName')),
      'Deposit token name is required',
    )
    rowCheck(
      'tokenSymbol',
      Boolean(value('tokenSymbol')),
      'Deposit token symbol is required',
    )
    if (!isStored || options.addressesEditable) {
      rowCheck(
        'depositAddress',
        isValidWalletAddress(value('depositAddress')),
        'Enter a valid deposit contract address',
      )
      rowCheck(
        'tokenAddress',
        isValidWalletAddress(value('tokenAddress')),
        'Enter a valid deposit token address',
      )
    }
    if (isStored) return

    rowCheck(
      'chainId',
      isPositiveInt(row?.chainId),
      'Enter a valid positive EVM chain ID',
    )
    if (value('trackId') && !/^\d+$/.test(value('trackId')))
      errors[`v1DepositConfigs.${index}.trackId`] = 'Enter a whole number'

    const key = chainKey(row?.chainId, row?.depositAddress)
    const addressField = `v1DepositConfigs.${index}.depositAddress`
    if (seenChains.has(key) && !errors[addressField])
      errors[addressField] =
        'This chain and deposit contract is already configured'
    seenChains.add(key)
  })

  if (options.tvl) {
    check(
      'v1DepositTokenPrice',
      isPositiveNumber(data.v1DepositTokenPrice),
      'Enter a deposit token price greater than 0',
    )
    check(
      'v1TvlTargetTVL',
      isPositiveNumber(data.v1TvlTargetTVL),
      'Enter a target TVL greater than 0',
    )
    const optionalNumber = (
      field: string,
      isValid: (value: unknown) => boolean,
      message: string,
    ) => {
      if (text(field) && !isValid(data[field])) errors[field] = message
    }
    optionalNumber(
      'v1TvlLockPeriodMonths',
      isNonNegativeNumber,
      'Enter the lock period in months (0 for none)',
    )
    optionalNumber(
      'v1TvlNativeYieldAPR',
      isNonNegativeNumber,
      'Enter the native yield APR as a number',
    )
    optionalNumber(
      'v1TvlFees',
      isNonNegativeNumber,
      'Enter fees as a number, or leave blank for 0',
    )
    optionalNumber(
      'v1TvlEarlyExitPenalty',
      isNonNegativeNumber,
      'Enter the early exit penalty as a number, or leave blank for 0',
    )
    optionalNumber(
      'v1TvlRewardTokenFdv',
      isPositiveNumber,
      'Enter an FDV greater than 0',
    )
    optionalNumber(
      'v1TvlRewardTokenTotalSupply',
      isPositiveNumber,
      'Enter a total supply greater than 0',
    )

    // Each reward kind needs only the inputs the storefront computes from.
    const rewardKind = text('v1TvlRewardKind')
    if (rewardKind === 'percentPerMonth') {
      check(
        'v1TvlRewardsPerMonth',
        isNonNegativeNumber(data.v1TvlRewardsPerMonth),
        'Enter the monthly reward rate as a number',
      )
      check(
        'v1TvlLockPeriodMonths',
        isNonNegativeNumber(data.v1TvlLockPeriodMonths),
        'Enter the lock period in months (0 for none)',
      )
      check(
        'v1TvlRewardTokenFdv',
        isPositiveNumber(data.v1TvlRewardTokenFdv),
        'Enter an FDV greater than 0',
      )
      check(
        'v1TvlRewardTokenTotalSupply',
        isPositiveNumber(data.v1TvlRewardTokenTotalSupply),
        'Enter a total supply greater than 0',
      )
    }
    if (rewardKind === 'fixedRatio') {
      check(
        'v1TvlTokensPerDepositUnit',
        isPositiveNumber(data.v1TvlTokensPerDepositUnit),
        'Enter the reward tokens per deposit token, greater than 0',
      )
      // The simulator prices the token as FDV / supply, so it needs both.
      if (!options.edit) {
        if (text('v1TvlRewardTokenFdv') && !text('v1TvlRewardTokenTotalSupply'))
          errors.v1TvlRewardTokenTotalSupply =
            'Enter the total supply too, or clear the FDV'
        if (text('v1TvlRewardTokenTotalSupply') && !text('v1TvlRewardTokenFdv'))
          errors.v1TvlRewardTokenFdv =
            'Enter the FDV too, or clear the total supply'
      }
    }
    if (rewardKind === 'percentPerMonth' || rewardKind === 'fixedRatio')
      check(
        'v1TvlRewardTokenSymbol',
        Boolean(text('v1TvlRewardTokenSymbol')),
        'Reward token symbol is required',
      )
    if (rewardKind === 'unquantified')
      check(
        'v1TvlRewardLabel',
        Boolean(text('v1TvlRewardLabel')),
        'Enter the text shown in place of a reward rate',
      )
    if (rewardKind === 'weeklyPool') {
      const weeks: any[] = Array.isArray(data.v1TvlWeeklyPool)
        ? data.v1TvlWeeklyPool
        : []
      if (!weeks.length && !options.edit)
        errors.v1TvlWeeklyPool = 'Add at least one week'
      weeks.forEach((week, index) => {
        const value = (field: string) => String(week?.[field] || '').trim()
        const weekCheck = (
          field: string,
          isValid: boolean,
          message: string,
        ) => {
          if (isValid || (options.edit && !value(field))) return
          errors[`v1TvlWeeklyPool.${index}.${field}`] = message
        }
        weekCheck(
          'baseUsd',
          isNonNegativeNumber(week?.baseUsd),
          'Enter the base pool in USD',
        )
        // A boost needs both its threshold and its amount.
        const hasBoost = value('boostTvlThresholdUsd') || value('boostUsd')
        if (hasBoost) {
          weekCheck(
            'boostTvlThresholdUsd',
            isPositiveNumber(week?.boostTvlThresholdUsd),
            'Enter the TVL threshold in USD that unlocks the boost',
          )
          weekCheck(
            'boostUsd',
            isPositiveNumber(week?.boostUsd),
            'Enter the boosted pool in USD',
          )
        }
      })
    }
  }

  for (const field of options.skipFields ?? []) delete errors[field]

  const isValid = Object.keys(errors).length === 0
  return {
    errors,
    isValid,
    message: isValid ? undefined : 'Please fix the highlighted fields.',
  }
}

export function validateErc4626Vault(data: any): {
  errors: Record<string, string>
  isValid: boolean
  message?: string
} {
  const errors: Record<string, string> = {}
  const chainId = Number(String(data.vaultChainId || '').trim())
  const decimals = Number(String(data.depositTokenDecimals || '').trim())
  const vaultAddress = String(data.vaultAddress || '').trim()
  const assetAddress = String(data.depositTokenAddress || '').trim()
  const externalUrl = String(data.vaultExternalUrl || '').trim()
  const depositStart = String(data.purchaseStartDate || '').trim()

  if (!depositStart || Number.isNaN(new Date(`${depositStart}Z`).getTime())) {
    errors.purchaseStartDate = 'Enter a valid deposit start date and time'
  }

  if (
    !Number.isSafeInteger(chainId) ||
    chainId <= 0 ||
    String(chainId) !== String(data.vaultChainId || '').trim()
  ) {
    errors.vaultChainId = 'Enter a valid positive EVM chain ID'
  }

  if (!isValidWalletAddress(vaultAddress)) {
    errors.vaultAddress = 'Enter a valid ERC-4626 vault address'
  }

  if (!isValidWalletAddress(assetAddress)) {
    errors.depositTokenAddress = 'Enter a valid underlying asset address'
  }

  if (!String(data.depositTokenName || '').trim()) {
    errors.depositTokenName = 'Underlying asset name is required'
  }

  if (!String(data.depositTokenSymbol || '').trim()) {
    errors.depositTokenSymbol = 'Underlying asset symbol is required'
  }

  if (
    !Number.isInteger(decimals) ||
    decimals < 0 ||
    decimals > 255 ||
    String(decimals) !== String(data.depositTokenDecimals || '').trim()
  ) {
    errors.depositTokenDecimals =
      'Enter underlying asset decimals from 0 to 255'
  }

  if (!String(data.vaultProtocolName || '').trim()) {
    errors.vaultProtocolName = 'Protocol name is required'
  }

  if (externalUrl && !isValidHttpsUrl(externalUrl)) {
    errors.vaultExternalUrl = 'Enter a valid HTTPS URL'
  }

  const isValid = Object.keys(errors).length === 0
  return {
    errors,
    isValid,
    message: isValid
      ? undefined
      : 'Please complete all required vault fields before submitting.',
  }
}

const toSafeNumber = (value: unknown): number => {
  if (value === null || value === undefined) return NaN
  const normalized = String(value).replace(/[^0-9.]/g, '')
  if (!normalized) return NaN
  const parsed = Number(normalized)
  return Number.isFinite(parsed) ? parsed : NaN
}

export function validateDealOverview(data: any): {
  errors: Record<string, string>
  isValid: boolean
  message?: string
} {
  const errors: Record<string, string> = {}

  if (!String(data.projectName || '').trim())
    errors.projectName = 'Project name is required'
  if (!data.salesType) (errors as any).salesType = 'Sales type is required'
  if (data.salesType === REQUEST_BESPOKE_VALUE) {
    if (!String(data.salesTypeDetail || '').trim())
      (errors as any).salesTypeDetail = 'Please describe the sales type'
  }
  if (data.salesType === 'OTC Deal') {
    if (!String(data.salesTypeDetail || '').trim())
      (errors as any).salesTypeDetail = 'Please describe the OTC deal'
  }
  if (data.salesType === LOCKDROPS_VALUE) {
    if (!String(data.salesTypeDetail || '').trim())
      (errors as any).salesTypeDetail = 'Please describe the lockdrop deal'
  }
  if (data.salesType !== 'OTC Deal') {
    if (!String(data.jurisdiction || '').trim())
      errors.jurisdiction = 'Jurisdiction is required'
    if (
      String(data.jurisdiction || '').trim() === 'other' &&
      !String((data as any).jurisdictionOther || '').trim()
    ) {
      ;(errors as any).jurisdictionOther = 'Please specify the jurisdiction'
    }
  }
  if (!data.logo) errors.logo = 'Logo upload is required'

  const website = (data.urls || []).find((u: any) => u.type === 'website')
  const x = (data.urls || []).find((u: any) => u.type === 'x')
  if (!website || !String(website.url || '').trim())
    errors['url-website'] = 'Official Site URL is required'
  else if (!website.url.startsWith('https://'))
    errors['url-website'] = 'Official Site URL must start with https://'
  if (!x || !String(x.url || '').trim())
    errors['url-x'] = 'X (Twitter) URL is required'
  else {
    const xErr = getUrlErrorMessage(String(x.url), 'x')
    if (xErr) errors['url-x'] = xErr
  }

  ;(data.urls || []).forEach((urlItem: any) => {
    if (
      urlItem &&
      urlItem.url &&
      urlItem.type !== 'website' &&
      urlItem.type !== 'x'
    ) {
      const err = getUrlErrorMessage(urlItem.url, urlItem.type)
      if (err) errors[`url-${urlItem.id}`] = err
    }
  })

  const isValid = Object.keys(errors).length === 0
  return {
    errors,
    isValid,
    message: isValid
      ? undefined
      : 'Please complete all required fields before continuing.',
  }
}

export function validateProjectSummary(
  data: any,
  requiresPublicPrivateDocs = false,
  salesType?: string,
): {
  errors: Record<string, string>
  isValid: boolean
  message?: string
} {
  const errors: Record<string, string> = {}

  const requireField = (field: string, label: string) => {
    const val = String((data as Record<string, unknown>)[field] || '').trim()
    const stripped = val
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, ' ')
      .trim()
    if (!val || !stripped) {
      errors[field] = `${label} is required`
    }
  }

  requireField('description', 'Project Narrative')

  const isOtcDeal = salesType === 'OTC Deal'
  if (!isOtcDeal) {
    const researchReportValue = String(data.researchReport || '').trim()
    if (requiresPublicPrivateDocs) {
      if (data.researchPath === 'link') {
        if (!researchReportValue)
          errors.researchReport = 'Research report link is required.'
        else if (!researchReportValue.startsWith('https://'))
          errors.researchReport = 'Research report URL must start with https://'
      } else if (data.researchPath !== 'outsourced') {
        errors.researchReport =
          'Select how you will provide research materials.'
      }
      if (!data.pitchDeck) errors.pitchDeck = 'Pitch deck PDF is required.'
    } else if (
      data.researchPath === 'link' &&
      researchReportValue &&
      !researchReportValue.startsWith('https://')
    ) {
      errors.researchReport = 'Research report URL must start with https://'
    }

    const requiresCapTable = requiresPublicPrivateDocs
    if (requiresCapTable) {
      const hasInvestor =
        Array.isArray(data.investors) &&
        data.investors.some(
          (investor: unknown) => String(investor || '').trim().length > 0,
        )
      if (!hasInvestor) (errors as any).capTable = 'Add at least one investor.'
    }
  }

  const isValid = Object.keys(errors).length === 0
  return {
    errors,
    isValid,
    message: isValid
      ? undefined
      : 'Please complete all required fields before continuing.',
  }
}

export function validateInvestmentTerms(data: any): {
  errors: Record<string, string>
  isValid: boolean
  message?: string
} {
  const errors: Record<string, string> = {}

  const isOtcDeal = data.salesType === 'OTC Deal'

  if (!isOtcDeal && !data.distributionChain)
    errors.distributionChain = 'Distribution chain is required'

  if (!isOtcDeal && !data.depositChain)
    errors.depositChain = 'Deposit chain is required'

  if (!isOtcDeal && !data.depositChainStablecoin)
    errors.depositChainStablecoin = 'Deposit chain stablecoin is required'

  if (!isOtcDeal && data.salesType !== LOCKDROPS_VALUE && !data.pricingMethod)
    errors.pricingMethod = 'Pricing method is required'

  if (!isOtcDeal && data.salesType !== LOCKDROPS_VALUE) {
    if (!String((data as any).projectTokenAddress || '').trim())
      errors.projectTokenAddress = 'Project token address is required'
    if (!String((data as any).projectTokenSymbol || '').trim())
      errors.projectTokenSymbol = 'Token symbol is required'
    if (!String((data as any).projectTokenName || '').trim())
      errors.projectTokenName = 'Token name is required'
    if (!String((data as any).projectTokenDecimals || '').trim())
      errors.projectTokenDecimals = 'Token decimals are required'
  }
  if (!isOtcDeal && !String(data.tokenSupply || '').trim())
    errors.tokenSupply = 'Total token supply is required'

  const minRaiseRaw = String(data.minimumRaiseThreshold || '').trim()
  if (!isOtcDeal && data.salesType !== LOCKDROPS_VALUE && !minRaiseRaw)
    errors.minimumRaiseThreshold = 'Minimum raise is required'
  if (
    !isOtcDeal &&
    data.salesType !== LOCKDROPS_VALUE &&
    minRaiseRaw &&
    Number(minRaiseRaw.replace(/[^0-9.]/g, '')) < 20000
  )
    errors.minimumRaiseThreshold =
      'Minimum raise threshold must be at least $20,000'
  if (!isOtcDeal && !String(data.minimumInvestmentPerUser || '').trim())
    errors.minimumInvestmentPerUser = 'Minimum investment per user is required'

  const minRaiseVal = Number(minRaiseRaw.replace(/[^0-9.]/g, ''))
  const maxRaiseRaw = (data as any).maxRaise ?? ''
  const maxRaiseStr = String(maxRaiseRaw).trim()
  const maxRaiseVal = Number(maxRaiseStr.replace(/[^0-9.]/g, ''))
  const fdvVal = Number(String(data.fdv || '').replace(/[^0-9.]/g, ''))
  if (!isOtcDeal && data.salesType !== LOCKDROPS_VALUE && !maxRaiseStr)
    (errors as any).maxRaise = 'Max raise is required'
  if (
    !isOtcDeal &&
    minRaiseRaw &&
    maxRaiseStr &&
    isFinite(minRaiseVal) &&
    isFinite(maxRaiseVal) &&
    maxRaiseVal < minRaiseVal
  ) {
    ;(errors as any).maxRaise =
      'Max Raise must be greater than or equal to Minimum Raise'
  }

  if (
    !isOtcDeal &&
    data.salesType !== LOCKDROPS_VALUE &&
    data.pricingMethod === 'fixed'
  ) {
    if (!String(data.fdv || '').trim()) errors.fdv = 'FDV is required'
    const fixedPoolName = String(data.fixedPoolName || '').trim()
    if (!fixedPoolName) {
      errors.fixedPoolName = 'Pool name is required'
    } else if (!FIXED_POOL_NAME_PRESETS.includes(fixedPoolName)) {
    }
    if (!String(data.tgeRelease || '').trim())
      errors.tgeRelease = 'TGE release is required'
    if (!(String(data.cliff ?? '') || data.cliff === '0'))
      errors.cliff = 'Cliff period is required'
    if (!(String(data.linearVesting ?? '') || data.linearVesting === '0'))
      errors.linearVesting = 'Linear vesting is required'
    if (
      minRaiseVal > 0 &&
      isFinite(minRaiseVal) &&
      isFinite(fdvVal) &&
      fdvVal > 0 &&
      minRaiseVal > fdvVal
    ) {
      errors.totalAllocation =
        'Total allocation exceeds total token supply. Reduce the minimum raise or increase FDV.'
    }
  }

  if (
    !isOtcDeal &&
    data.salesType !== LOCKDROPS_VALUE &&
    data.pricingMethod === 'flexible'
  ) {
    const schedules: FlexibleSchedule[] = data.flexibleSchedules || []
    if (schedules.length < 2) errors.flexible = 'Add at least two options'
    const nameCounts: Record<string, number> = {}
    schedules.forEach((s) => {
      const key = (s.name || '').trim().toLowerCase()
      if (!key) return
      nameCounts[key] = (nameCounts[key] || 0) + 1
    })
    schedules.forEach((s) => {
      const trimmedName = (s.name || '').trim()
      const isCustomName =
        !FLEXIBLE_POOL_NAME_PRESETS.includes(trimmedName) && trimmedName !== ''
      if (!trimmedName) {
        errors[`flexible-${s.id}-name`] = 'Pool name is required'
      } else if (isCustomName) {
      } else {
        const key = trimmedName.toLowerCase()
        if (nameCounts[key] > 1)
          errors[`flexible-${s.id}-name`] = 'Pool name must be unique'
      }
      if (!String(s.tgeRelease || '').trim())
        errors[`flexible-${s.id}-tgeRelease`] = 'TGE release is required'
      if (!String(s.fdv || '').trim())
        errors[`flexible-${s.id}-fdv`] = 'FDV is required'
      if (!(String(s.cliff ?? '') || s.cliff === '0'))
        errors[`flexible-${s.id}-cliff`] = 'Cliff period is required'
      if (!(String(s.linearVesting ?? '') || s.linearVesting === '0'))
        errors[`flexible-${s.id}-linearVesting`] = 'Linear vesting is required'

      if (s.enableBuybackProtection) {
        if (!String(s.buybackPaymentToken || '').trim()) {
          errors[`flexible-${s.id}-buybackPaymentToken`] =
            'Payment token is required'
        }
        if (!String(s.buybackPrice || '').trim()) {
          errors[`flexible-${s.id}-buybackPrice`] = 'Price is required'
        }
        const buybackAddress = String(s.fundingWallet || '').trim()
        if (!buybackAddress) {
          errors[`flexible-${s.id}-fundingWallet`] =
            'Funding wallet is required'
        } else if (!isValidWalletAddress(buybackAddress)) {
          errors[`flexible-${s.id}-fundingWallet`] =
            'Please enter a valid ETH/EVM wallet address'
        }
        if (!String(s.buybackStartDate || '').trim()) {
          errors[`flexible-${s.id}-buybackStartDate`] = 'Start date is required'
        }
        if (!String(s.buybackEndDate || '').trim()) {
          errors[`flexible-${s.id}-buybackEndDate`] = 'End date is required'
        }
        const startDateStr = String(s.buybackStartDate || '').trim()
        const endDateStr = String(s.buybackEndDate || '').trim()
        if (startDateStr && endDateStr) {
          const startDate = new Date(startDateStr)
          const endDate = new Date(endDateStr)
          if (endDate <= startDate) {
            errors[`flexible-${s.id}-buybackEndDate`] =
              'End date must be later than start date'
          }
        }
      }
    })
    if (minRaiseVal > 0 && isFinite(minRaiseVal)) {
      const reciprocalSum = schedules.reduce((sum, schedule) => {
        const scheduleFdv = toSafeNumber(schedule.fdv)
        if (!isFinite(scheduleFdv) || scheduleFdv <= 0) return sum
        return sum + 1 / scheduleFdv
      }, 0)

      if (reciprocalSum > 0 && minRaiseVal * reciprocalSum > 1) {
        errors.totalAllocation =
          'Combined pool allocations exceed the total token supply. Lower the minimum raise or adjust pool FDVs.'
      }
    }
  }

  if (data.salesType !== LOCKDROPS_VALUE && data.enableBuybackProtection) {
    if (!String(data.buybackPaymentToken || '').trim()) {
      errors.buybackPaymentToken = 'Payment token is required'
    }
    if (!String(data.buybackPrice || '').trim()) {
      errors.buybackPrice = 'Price is required'
    }
    const buybackAddress = String(data.fundingWallet || '').trim()
    if (!buybackAddress) {
      errors.fundingWallet = 'Funding wallet is required'
    } else if (!isValidWalletAddress(buybackAddress)) {
      errors.fundingWallet = 'Please enter a valid ETH/EVM wallet address'
    }
    if (!String(data.buybackStartDate || '').trim()) {
      errors.buybackStartDate = 'Start date is required'
    }
    if (!String(data.buybackEndDate || '').trim()) {
      errors.buybackEndDate = 'End date is required'
    }
    const startDateStr = String(data.buybackStartDate || '').trim()
    const endDateStr = String(data.buybackEndDate || '').trim()
    if (startDateStr && endDateStr) {
      const startDate = new Date(startDateStr)
      const endDate = new Date(endDateStr)
      if (endDate <= startDate) {
        errors.buybackEndDate = 'End date must be later than start date'
      }
    }
  }

  if (data.salesType === LOCKDROPS_VALUE) {
    const lockdropDuration = String(data.lockdropDuration || '').trim()
    if (!lockdropDuration) {
      errors.lockdropDuration = 'Lockdrop duration is required'
    }

    if (lockdropDuration === 'custom') {
      if (!String(data.lockdropStartDate || '').trim()) {
        errors.lockdropStartDate = 'Start date is required'
      }
      if (!String(data.lockdropEndDate || '').trim()) {
        errors.lockdropEndDate = 'End date is required'
      }

      const startDateStr = String(data.lockdropStartDate || '').trim()
      const endDateStr = String(data.lockdropEndDate || '').trim()
      if (startDateStr && endDateStr) {
        const startDate = new Date(startDateStr)
        const endDate = new Date(endDateStr)
        if (endDate <= startDate) {
          errors.lockdropEndDate = 'End date must be later than start date'
        }
      }
    }

    if (!String(data.targetTVL || '').trim()) {
      errors.targetTVL = 'Target TVL is required'
    }

    if (!String(data.targetYieldProposal || '').trim()) {
      errors.targetYieldProposal = 'Target yield proposal is required'
    }

    if (!String(data.fdvLastRound || '').trim()) {
      errors.fdvLastRound = 'FDV of last round is required'
    }
  }

  const isValid = Object.keys(errors).length === 0
  return {
    errors,
    isValid,
    message: isValid
      ? undefined
      : 'Please complete all required fields for the selected pricing method.',
  }
}
